<template>
  <div class="w-100">
    <ThemeSelector />
    <PulsBackground background-variant="8">
      <NavBar
        v-if="userJwt"
        current-item="results"
        :auto-dark-theme="true"
        :on-click-item="onClickNavItem"
        :show-logo="true"
        :submenu-items="[]"
        :user-is-admin="userIsAdmin"
      />

      <div v-if="debug" class="container app-container pt-10">
        <div class="alert alert-info">
          <table>
            <tbody>
              <tr>
                <td>tokenTimeLeft:</td>
                <td>{{ (tokenTimeLeft / 60).toFixed(0) }}m</td>
              </tr>
              <tr>
                <td>tokenIsExpired:</td>
                <td>{{ tokenIsExpired }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div v-if="realtimeEnabled && !!userJwt" class="card">
        <PulsRealtime
          app="results"
          :auth="userJwt"
          :debug="realtimeDebug"
          :environment="environment"
          :messageReceivedCallback="realtimeMessageReceived"
          :connectedCallback="realtimeConnectedCallback"
          :disconnectedCallback="realtimeDisconnectedCallback"
          class="ms-3 me-3"
        />
      </div>
      <div v-if="hasCheckedAuth" class="container app-container pb-20">
        <RouterView />
      </div>
    </PulsBackground>
  </div>
</template>

<script>
import { useRouter } from 'vue-router'
import { PulsRealtime } from '@puls-solutions/puls-realtime-vue'
import { NavBar } from '@puls-solutions/puls-shared-menu-vue3'
import { configToUse, getEnvironment } from '@puls-solutions/puls-devops-utils'
import { currentCredentials } from '@/services/aws-amplify-service'
import { useUserStore } from './stores/user-store'
import { useStatesStore } from './stores/states-store'
import { useRealtimeStore } from './stores/realtime-store'
import { Amplify } from 'aws-amplify'
import { Tooltip, Dropdown } from 'bootstrap'
import config from './config'

export default {
  name: 'App',
  components: {
    PulsRealtime,
    NavBar
  },
  setup() {
    const userStore = useUserStore()
    const statesStore = useStatesStore()
    const realtimeStore = useRealtimeStore()
    const router = useRouter()
    const routes = router.getRoutes()
    const baseRoutes = routes.filter((route) => !route.path.includes(':'))
    return { routes: baseRoutes, realtimeStore, userStore, statesStore }
  },
  data() {
    return {
      hasCheckedAuth: false,
      dropdownDelegationHandler: null
    }
  },
  computed: {
    environment() {
      return getEnvironment({})
    },
    user() {
      return this.userStore.loggedInUser
    },
    hasToken() {
      return this.userStore.hasToken
    },
    realtimeEnabled() {
      return this.realtimeStore.realtimeEnabled
    },
    realtimeDebug() {
      // check if query params includes 'debug'
      return this.$route.query.realtimedebug === 'true'
    },
    routesForUser() {
      return this.routes.filter((route) => {
        if (route.meta.authentication) {
          return this.user
        }
        return true
      })
    },
    tokenTimeLeft() {
      return this.userStore.getTimeLeftBeforeTokenRefresh
    },
    tokenIsExpired() {
      return this.userStore.getTokenIsExpired
    },
    userIsAdmin() {
      return this.userStore.userIsPulsAdmin || this.userStore.userIsGroupAdmin
    },
    userPoolNotConfiguredError() {
      return this.userStore?.getError?.name === 'AuthUserPoolException'
    },
    userJwt() {
      return this.userStore?.loggedInUser?.session?.idToken.toString()
    },
    realtimeError() {
      return this.realtimeEnabled && this.realtimeStore.realtimeError
    },
    debug() {
      return this.$route.query.debug
    }
  },
  watch: {
    userPoolNotConfiguredError() {
      if (this.userPoolNotConfiguredError) {
        window.location = '/#/start'
      }
    },
    realtimeError(e) {
      if (e && e.message === 'ExpiredTokenException') {
        // trigger reauth

        const orgConfig = this.userStore.getOrgConfig
        if (
          orgConfig &&
          orgConfig.userPoolId &&
          this.userStore.getLastAuthUser
        ) {
          this.refreshCredentials(orgConfig)
        }
      }
    }
  },
  mounted() {
    new Tooltip(document.body, {
      selector: "[data-bs-toggle='tooltip']"
    })
    this.initializeDropdowns()
  },
  beforeUnmount() {
    if (this.dropdownDelegationHandler) {
      document.removeEventListener('click', this.dropdownDelegationHandler)
      this.dropdownDelegationHandler = null
    }
  },
  created() {
    this.initializeAmplify()
    const orgConfig = this.userStore.getOrgConfig
    if (orgConfig && orgConfig.userPoolId && this.userStore.getLastAuthUser) {
      this.refreshCredentials(orgConfig)
    } else {
      this.hasCheckedAuth = true
    }
  },
  methods: {
    initializeDropdowns() {
      this.dropdownDelegationHandler = (event) => {
        const toggleEl = event.target.closest("[data-bs-toggle='dropdown']")
        if (!toggleEl || toggleEl.disabled) {
          return
        }
        event.preventDefault()
        Dropdown.getOrCreateInstance(toggleEl).toggle()
      }
      document.addEventListener('click', this.dropdownDelegationHandler)
    },
    initializeAmplify() {
      const c = configToUse({ config })
      const resourcesConfig = {
        API: {
          REST: {
            ActionsAPI: {
              endpointType: 'REST',
              endpoint: c.actionsApiUrl,
              region: 'eu-west-1'
            },
            InsightsAPI: {
              endpointType: 'REST',
              endpoint: c.insightsApiUrl,
              region: 'eu-west-1'
            },
            PublicAPI: {
              endpointType: 'REST',
              endpoint: c.publicApiUrl,
              region: 'eu-west-1'
            },
            ResultsAPI: {
              endpointType: 'REST',
              endpoint: c.resultsApiUrl,
              region: 'eu-west-1'
            },
            SurveyAPI: {
              endpointType: 'REST',
              endpoint: c.surveyApiUrl,
              region: 'eu-west-1'
            },
            TeamAPI: {
              endpointType: 'REST',
              endpoint: c.teamApiUrl,
              region: 'eu-west-1'
            }
          }
        }
      }
      this.statesStore.setApiKey(c.apiKey)
      Amplify.configure(resourcesConfig)
    },
    refreshCredentials(orgConfig) {
      const existingConfig = Amplify.getConfig()
      const auth = {
        Auth: {
          Cognito: {
            region: 'eu-west-1',
            userPoolId: orgConfig.userPoolId,
            userPoolClientId: orgConfig.userPoolWebClientId
          },
          clientMetadata: {
            organizationName: orgConfig.organizationName,
            organizationFullName: orgConfig.organizationFullName,
            locale: 'en'
          }
        }
      }
      Amplify.configure({ ...existingConfig, ...auth })
      return currentCredentials()
        .then((user) => {
          this.userStore.setLoggedInUser(user)
          this.userStore.setHasToken(true)
          const {
            organizationName,
            organizationFullName,
            organizationFeatures
          } = user.session.idToken.payload
          this.statesStore.setOrganizationName(organizationName)
          if (organizationFeatures) {
            this.statesStore.setOrganizationFeatures(
              JSON.parse(organizationFeatures)
            )
          }
          const organizationGroup = {
            name: organizationName,
            messages: {
              en: {
                title: this.$t('general.entireOrganization')
              }
            }
          }
          this.statesStore.setOrganizationGroup(organizationGroup)
          this.statesStore.setSelectedGroup(organizationGroup)
          if (orgConfig.attributes) {
            const statesStore = useStatesStore()
            statesStore.setAttributesForResults(orgConfig.attributes)
          }
        })
        .catch((error) => {
          console.error(error)
          this.userStore.setLoggedInUser(null)
          this.userStore.setHasToken(false)
          // this.$router.push('/login')
          window.location = '/#/start'
        })
        .finally(() => {
          this.hasCheckedAuth = true
        })
    },
    realtimeMessageReceived(msg) {
      if (msg.action === 'reload') {
        window.location.reload()
      } else if (msg.action === 'alert') {
        alert(msg.message)
      } else if (msg.action === 'logout') {
        window.location.href = '/#/logout'
      } else if (msg.action === 'messages') {
        this.realtimeStore.addRealtimeMessageAction({
          message: msg
        })
      } else if (msg.action === 'error') {
        this.realtimeStore.setRealtimeError(msg)
        // this.$store.commit('setRealtimeError', msg)
      }
    },
    realtimeConnectedCallback(msg) {
      this.realtimeStore.setRealtimeConnectionId({
        connectionId: msg.connectionId
      })
    },
    realtimeDisconnectedCallback() {
      this.realtimeStore.setRealtimeConnectionId({ connectionId: null })
    },
    onClickNavItem(item) {
      window.location.href = item.link
    }
  }
}
</script>

<style lang="scss">
$selectorWidthSmall: 180px;
$selectorWidthMedium: 220px;
$selectorWidthLarge: 300px;

@media (min-width: 992px) {
  .app-container {
    width: 90%;
    max-width: min(90%, 1400px);
  }
}
.cursor-pointer {
  cursor: pointer !important;
}
.cursor-help {
  cursor: help !important;
}

.hover-primary:hover {
  color: var(--bs-primary) !important; /* or background-color */
}

.swiper {
  z-index: 0 !important;
}

.multiselect__option {
  font-size: 12px;
}

.puls-multi-selector {
  min-width: $selectorWidthLarge;
  max-width: $selectorWidthLarge;
  .multiselect--active {
    .puls-multi-selector__select {
      transform: rotateZ(180deg);
    }
  }
  .multiselect__content-wrapper /*, .multiselect__element */ {
    width: fit-content;
    min-width: 100%;
    background-color: var(--kt-gray-100);
  }
  &-small {
    $small-height: 25px;
    min-width: 220px;
    .multiselect {
      font-size: 14px;
      min-height: $small-height;
      max-height: $small-height;
    }
    .multiselect__single {
      font-size: 14px;
    }
    .puls-multi-selector__select {
      line-height: 14px;
      height: $small-height;
    }
    .multiselect__tags {
      padding: 1px 40px 0 8px;
      min-height: $small-height;
      max-height: $small-height;
    }
    .multiselect--above .multiselect__content-wrapper {
      bottom: inherit;
      transform: translateY(calc(-100% - 24px));
    }
    .multiselect__content-wrapper {
      position: fixed;
      // display: block !important;
      transform: translate(0, 0);
      min-width: $selectorWidthLarge;
      max-width: $selectorWidthLarge;
      @media (max-width: 1400px) {
        min-width: $selectorWidthMedium;
        max-width: $selectorWidthMedium;
      }
      @media (max-width: 1200px) {
        min-width: $selectorWidthSmall;
        max-width: $selectorWidthSmall;
      }
    }
  }
  &__select {
    line-height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    box-sizing: border-box;
    width: 40px;
    height: 38px;
    right: 1px;
    top: 1px;
    padding: 4px 8px;
    margin: 0;
    text-decoration: none;
    text-align: center;
    cursor: pointer;
    transition: transform 0.2s ease;
  }
  .multiselect__tags {
    color: var(--kt-input-color);
    background-color: var(--kt-input-bg);
    border: 1px solid var(--kt-input-border-color);
    box-shadow: none !important;
    border: 1px solid #efe9e1;
    appearance: none;
    border-radius: 0.95rem;
  }
  .multiselect__option {
    color: var(--kt-text-dark) !important;
  }
  .multiselect--disabled {
    border-radius: 0.95rem;
    background: var(--kt-input-disabled-bg) !important;
  }
  .multiselect__option--disabled {
    background-color: var(--kt-input-disabled-bg) !important;
  }
  .multiselect__option--selected {
    font-weight: inherit !important;
    background-color: var(--kt-primary-light) !important;
  }

  .multiselect__option--highlight {
    color: var(--kt-text-dark);
    background-color: var(--kt-secondary) !important;
  }

  .multiselect__element,
  .multiselect__input,
  // .multiselect__option,
  .multiselect__single {
    color: var(--kt-text-dark);
    background-color: var(--kt-input-bg) !important;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  .multiselect__content {
    color: var(--kt-text-dark);
  }
}

@media (max-width: 1400px) {
  .puls-multi-selector {
    min-width: $selectorWidthMedium;
    max-width: $selectorWidthMedium;
  }
}

@media (max-width: 1200px) {
  .puls-multi-selector {
    min-width: $selectorWidthSmall;
    max-width: $selectorWidthSmall;
  }
}
@media (max-width: 576px) {
  .puls-multi-selector {
    min-width: 100%;
    max-width: 100%;
    width: 100%;
    flex: 1 1 auto;
  }

  .multi-select-parent {
    width: 100%;
  }
}
</style>

<!-- Tree selector overrides -->
<style lang="scss">
.vue-treeselect {
  &--open {
    .vue-treeselect__control {
      border-top-left-radius: 0.95rem;
      border-top-right-radius: 0.95rem;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
  &--open-below {
    .vue-treeselect__control {
      border-top-left-radius: 0.95rem;
      border-top-right-radius: 0.95rem;
      border-bottom-left-radius: 0.95rem;
      border-bottom-right-radius: 0.95rem;
    }
  }
  &__control {
    color: var(--kt-input-color) !important;
    background-color: var(--kt-input-bg) !important;
  }
  &__multi-value-item {
    color: var(--kt-secondary-inverse) !important;
    background-color: var(--kt-secondary) !important;
  }
  &__value-remove {
    color: var(--kt-secondary-inverse) !important;
    &:hover {
      color: var(--kt-danger) !important;
    }
  }
  &__x-container {
    &:hover {
      color: var(--kt-danger) !important;
    }
  }
  &__menu {
    background: var(--kt-input-bg) !important;
    overflow-x: auto !important;
  }
  &__label {
    overflow: visible;
  }
  .vue-treeselect__option {
    color: var(--kt-input-color);
    background-color: var(--kt-input-bg);
    &--highlight {
      background-color: var(--kt-secondary) !important;
      .vue-treeselect__label {
        color: var(--kt-text-dark);
      }
    }
    &--selected {
      background-color: var(--kt-secondary) !important;
      .vue-treeselect__label {
        color: var(--kt-secondary-inverse);
      }
    }
  }
  .vue-treeselect__option-arrow {
    color: var(--kt-dropdown-link-hover-color);
  }
  .vue-treeselect__label {
    color: var(--kt-input-color);
  }
  .vue-treeselect__single-value {
    color: var(--kt-input-color);
  }
  .vue-treeselect__option-arrow-container:hover .vue-treeselect__option-arrow,
  .vue-treeselect--branch-nodes-disabled
    .vue-treeselect__option:hover
    .vue-treeselect__option-arrow {
    color: var(--kt-dropdown-link-color);
  }
}
</style>
